import memoizeOne from 'memoize-one';
import React, { Component, Fragment, ReactNode } from 'react';
import Calculation from '~/AeMaxPrice/Calculation/Calculation';
import ParametersPanel, { PriceValue, Values as ReferenceValues } from '~/AeMaxPrice/ParametersPanel';
import ProductsTable, { ProductRow, ProductValues } from '~/AeMaxPrice/ProductsTable/ProductsTable';
import { Country } from '~/Api/Country';
import { ReferenceBasketVersion } from '~/Api/PriceReferencing';
import { Product, Product as GeneralProduct } from '~/Api/Product';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import {
	AeMaxPrice as ApiAeMaxPrice,
	CountryOfOrigin,
	GeneralInformation,
	Item,
	ProductUpdate,
	ReferenceUpdate,
	ValidationResult,
} from '~/Api/User/ProductGroup/AeMaxPrice';
import { ErrorCode } from '~/Api/User/ProductGroup/AeMaxPrice/Error';
import CopyGroupProductActionPanel from '~/CopyGroupProduct/CopyGroupProductActionPanel';
import parseHumanFloat from '~/libs/Number/parseHumanFloat';
import Price, { isValidPriceValue } from '~/libs/Price';
import { parsePackageSize, PricedProduct } from '~/Product';
import ProductDetailModal, { PricesContainer } from '~/ProductDetailModal';
import ProductGroupRemoveProductModal from '~/ProductGroupRemoveProductModal';
import ProductInstructions from '~/ProductInstructions';
import ActivityIndicator from '~/UI/ActivityIndicator';
import Heading from '~/UI/Heading/Heading';

import { ProductProvider } from './ProductProvider';
import styles from './styles.less';

interface Props {
	header: ReactNode;
	group: UserProductGroup;
	aeMaxPrice: ApiAeMaxPrice | null;
	referenceBasketVersion: ReferenceBasketVersion | null;
	referenceNationality: Country | null;
	pricedProducts: PricedProduct<Item>[];
	arabEmiratesProducts: ProductRow[];
	referenceBasketProducts: ProductRow[];
	otherProducts: ProductRow[];
	validationResult: ValidationResult | null;
	isFetching: boolean;
	detailProductId: string | null;
	detailProductTabName: string;
	currency: string | null;
	onFetch(group: UserProductGroup): Promise<void>;
	onPrune(group: UserProductGroup): void;
	productDetailLink(product: { id: string }): { pathname: string; search: string };
	onCloseDetail(): void;
	onShowDetail(productId: string, detailProductTabName: string): void;
	onUpdate(group: UserProductGroup, reference: ReferenceUpdate, products: ProductUpdate[]): void;
	onSave(group: UserProductGroup): void;
	onCurrencyChange(currency: string): void;
	selectNationality(name: string): Country | null;
	onDeleteItems(group: UserProductGroup, products: GeneralProduct[], onSuccess: () => void): Promise<void>;
}

interface Values {
	reference: ReferenceValues;
	product: { [key: string]: ProductValues };
}

interface State {
	values: Values | null;
	lastSelectedProductId: string | null;
	lastSeenProductIdDetail: string | null;
	removeProduct: Product | null;
	checkedRows: GeneralProduct[];
}

const EmptyPrice: PriceValue = { value: '', currency: 'USD', isValid: true };

export default class AeMaxPrice extends Component<Props, State> {
	static defaultProps = {
		aeMaxPrice: null,
		validationResult: null,
		detailProductId: null,
		detailProductTabName: 'info',
	};

	state = {
		values: null,
		lastSelectedProductId: null,
		lastSeenProductIdDetail: null,
		removeProduct: null,
		checkedRows: [],
	};

	componentDidMount() {
		const { group, onFetch } = this.props;
		onFetch(group);
	}

	componentWillUnmount() {
		const { group, onPrune } = this.props;
		onPrune(group);
	}

	componentDidUpdate(prevProps: Props) {
		const { group, validationResult, onFetch } = this.props;

		if (prevProps.group !== group) {
			onFetch(group);
		} else if (prevProps.validationResult !== validationResult) {
			this.processValidationResult(validationResult);
		}
	}

	resolveValues(): Values {
		const {
			props: { aeMaxPrice, referenceNationality },
			state: { values },
		} = this;
		return this.createValues(aeMaxPrice, referenceNationality, values);
	}

	readonly checkRow = (product: GeneralProduct, checked: boolean) => {
		this.setState((prevState) => ({
			checkedRows: checked
				? [...prevState.checkedRows, product]
				: prevState.checkedRows.filter((row) => row.id !== product.id),
		}));
	};

	readonly countRows = () => {
		return this.state.checkedRows.length;
	};

	onDeleteItems = async (group: UserProductGroup, products: GeneralProduct[]) => {
		await this.props.onDeleteItems(group, products, () => {
			this.setState({ checkedRows: [] });
		});
	};

	readonly onClickCancelDelete = () => {
		this.setState({ checkedRows: [] });
	};

	createValues = memoizeOne(
		(aeMaxPrice: ApiAeMaxPrice | null, referenceNationality: Country | null, values: Values | null): Values => {
			const mapPrice = (price: { value: string; currency: string } | null) =>
				price ? { value: price.value, currency: price.currency, isValid: true } : EmptyPrice;

			return {
				reference: values?.reference ?? {
					basketVersion: aeMaxPrice?.reference.referenceBasketVersion || '',
					companyName: aeMaxPrice?.reference.generalInformation.companyName || '',
					concentration: aeMaxPrice?.reference.generalInformation.concentration || '',
					packSize: aeMaxPrice?.reference.generalInformation.packSize || '',
					pharmaceuticalForm: aeMaxPrice?.reference.generalInformation.pharmaceuticalForm || '',
					productName: aeMaxPrice?.reference.generalInformation.productName || '',
					scientificName: aeMaxPrice?.reference.generalInformation.scientificName || '',
					proposedCifPrice: mapPrice(aeMaxPrice?.reference.proposedCifPrice || null),
					nationality: {
						value: referenceNationality?.name || '',
						isValid: true,
					},
					exFactoryPrice: mapPrice(aeMaxPrice?.reference.countryOfOrigin.exFactoryPrice || null),
					wholesalePrice: mapPrice(aeMaxPrice?.reference.countryOfOrigin.wholesalePrice || null),
					publicPrice: mapPrice(aeMaxPrice?.reference.countryOfOrigin.publicPrice || null),
				},
				product:
					aeMaxPrice?.products.reduce(
						(
							products: {
								[key: string]: {
									packageSizeForSingleUnit: {
										value: string;
										isValid: boolean | null;
									};
									exFactoryPrice: {
										value: { value: string | null; parentValue: null; prompt: React.ReactNode };
										isValid: boolean;
									};
									selected: boolean;
								};
							},
							product
						) => {
							const currentValues = values?.product[product.product.id];
							const hasSelectedValidExFactoryPrice =
								currentValues !== undefined &&
								(currentValues.exFactoryPrice.value?.value === null ||
									product.referencePrices.exFactory.find(
										(price) => price.calculatedPrice.type === currentValues.exFactoryPrice.value?.value
									) !== undefined);
							const exFactoryPrice = product.referencePrices.exFactory.find((price) => price.selected);

							products[product.product.id] = {
								packageSizeForSingleUnit: currentValues?.packageSizeForSingleUnit ?? {
									value: product.packageSizeForSingleUnit !== null ? String(product.packageSizeForSingleUnit) : '',
									isValid: true,
								},
								exFactoryPrice: hasSelectedValidExFactoryPrice
									? currentValues.exFactoryPrice
									: {
											value: {
												value: exFactoryPrice?.calculatedPrice.type ?? null,
												parentValue: null,
												prompt: null,
											},
											isValid:
												product.referencePrices.exFactory.length === 0 ||
												exFactoryPrice !== undefined ||
												product.allowsEmptyCalculator.exFactory,
									  },
								selected: Boolean(currentValues?.selected ?? product.selected),
							};
							return products;
						},
						{}
					) || {},
			};
		}
	);

	processValidationResult(validationResult: ValidationResult | null) {
		if (!validationResult) {
			return;
		}

		const values = this.resolveValues();
		const referenceValues = { ...values.reference };
		const productValues = { ...values.product };

		if (validationResult?.isMissingNationality()) {
			referenceValues.nationality = {
				...referenceValues.nationality,
				isValid: false,
			};
		}
		if (validationResult?.isMissingCifPrice()) {
			referenceValues.proposedCifPrice = {
				...referenceValues.proposedCifPrice,
				isValid: false,
			};
		}
		if (validationResult?.isMissingPrice()) {
			referenceValues.exFactoryPrice = {
				...referenceValues.exFactoryPrice,
				isValid: false,
			};
			referenceValues.wholesalePrice = {
				...referenceValues.wholesalePrice,
				isValid: false,
			};
			referenceValues.publicPrice = {
				...referenceValues.publicPrice,
				isValid: false,
			};
		}

		validationResult.productErrors.forEach((error) => {
			const id = error.productId;
			if (!id) {
				return;
			}

			const values = productValues[id];

			if (!values) {
				return;
			}
			if (error.code === ErrorCode.productSettingsMissing) {
				productValues[id] = {
					...values,
					packageSizeForSingleUnit: {
						...values.packageSizeForSingleUnit,
						isValid: false,
					},
				};
			} else if (error.code === ErrorCode.illegalPriceTypeSelected) {
				productValues[id] = {
					...values,
					exFactoryPrice: { ...values.exFactoryPrice, isValid: false },
				};
			}
		});

		this.setState({
			values: { reference: referenceValues, product: productValues },
		});
	}

	handleReferenceBasketVersionChange = (basketVersion: string) => {
		const values = this.resolveValues();

		this.setState(
			{
				values: {
					reference: { ...values.reference, basketVersion },
					product: values.product,
				},
			},
			() => {
				const { group, onSave } = this.props;
				this.update();
				onSave(group);
			}
		);
	};

	handleReferenceValueChange = (fieldName: string, value: PriceValue) => {
		const values = this.resolveValues();
		this.setState(
			{
				values: {
					reference: { ...values.reference, [fieldName]: value },
					product: values.product,
				},
			},
			this.update
		);
	};

	handleProductValuesChange = (
		item: Item,
		packageSizeForSingleUnit: string,
		exFactoryPrice: {
			value: string | null;
			parentValue: null;
			prompt: ReactNode;
		}
	) => {
		const values = this.resolveValues();
		const currentValue = values.product[item.product.id];

		this.setState(
			{
				values: {
					reference: values.reference,
					product: {
						...values.product,
						[item.product.id]: {
							packageSizeForSingleUnit: {
								value: packageSizeForSingleUnit,
								isValid: null,
							},
							exFactoryPrice: {
								value: exFactoryPrice,
								isValid:
									item.referencePrices.exFactory.length === 0 ||
									exFactoryPrice !== null ||
									item.allowsEmptyCalculator.exFactory,
							},
							selected: currentValue.selected,
						},
					},
				},
			},
			this.update
		);
	};

	productSelected = (item: Item, selected: boolean, range: boolean) => {
		const values = this.resolveValues();
		const currentValue = values.product[item.product.id];
		const productValues = { ...values.product };

		if (range) {
			this.selectProductRange(productValues, item.product.id, this.state.lastSelectedProductId, selected);
		} else {
			productValues[item.product.id] = { ...currentValue, selected };
		}

		this.setState(
			{
				values: { reference: values.reference, product: productValues },
				lastSelectedProductId: item.product.id,
			},
			this.update
		);
	};

	selectProductRange(
		productValues: { [key: string]: ProductValues },
		selectedProductId: string,
		lastSelectedProductId: string | null,
		selected: boolean
	) {
		const { pricedProducts } = this.props;
		const selectedIndex = pricedProducts.findIndex((pricedProduct) => pricedProduct.product.id === selectedProductId);
		const lastSelectedIndex = pricedProducts.findIndex(
			(pricedProduct) => pricedProduct.product.id === lastSelectedProductId
		);

		if (selectedIndex >= 0 && lastSelectedIndex >= 0) {
			const startIndex = Math.min(selectedIndex, lastSelectedIndex);
			const endIndex = Math.max(selectedIndex, lastSelectedIndex);
			for (let i = startIndex; i <= endIndex; i += 1) {
				const values = productValues[pricedProducts[i].product.id];
				productValues[pricedProducts[i].product.id] = { ...values, selected };
			}
		}
	}

	update = () => {
		const {
			props: { onUpdate, selectNationality },
		} = this;
		const values = this.resolveValues();

		let hasValidationError = false;

		const productValues = { ...values.product };
		const products = Object.keys(productValues).map((productId) => {
			const { packageSizeForSingleUnit, selected, exFactoryPrice } = productValues[productId];
			const parsedPackageSizeForSingleUnit = parsePackageSize(packageSizeForSingleUnit.value);

			productValues[productId] = {
				packageSizeForSingleUnit: {
					...packageSizeForSingleUnit,
					isValid: parsedPackageSizeForSingleUnit.isValid,
				},
				selected,
				exFactoryPrice,
			};
			hasValidationError = hasValidationError || parsedPackageSizeForSingleUnit.isValid === false;

			return new ProductUpdate(productId, parsedPackageSizeForSingleUnit.value, exFactoryPrice.value.value, selected);
		});

		const mapPrice = (price: { value: string; currency: string }): [Price | null, PriceValue] => {
			if (price.value === '') {
				return [null, { ...price, isValid: true }];
			}
			const parsedValue = parseHumanFloat(price.value);
			if (parsedValue === null || parsedValue === undefined || !isValidPriceValue(parsedValue)) {
				hasValidationError = true;
				return [null, { ...price, isValid: false }];
			}
			return [new Price(String(parsedValue), price.currency), { ...price, isValid: true }];
		};

		const referenceValues = { ...values.reference };

		const nationality = selectNationality(values.reference.nationality?.value);
		referenceValues.nationality = {
			...values.reference.nationality,
			isValid: values.reference.nationality.value === '' || Boolean(nationality),
		};
		hasValidationError = hasValidationError || !referenceValues.nationality.isValid;

		const [proposedCifPrice, proposedCifPriceValue] = mapPrice(values.reference.proposedCifPrice);
		referenceValues.proposedCifPrice = proposedCifPriceValue;

		const [exFactoryPrice, exFactoryPriceValue] = mapPrice(values.reference.exFactoryPrice);
		referenceValues.exFactoryPrice = exFactoryPriceValue;

		const [wholesalePrice, wholesalePriceValue] = mapPrice(values.reference.wholesalePrice);
		referenceValues.wholesalePrice = wholesalePriceValue;

		const [publicPrice, publicPriceValue] = mapPrice(values.reference.publicPrice);
		referenceValues.publicPrice = publicPriceValue;

		this.setState({
			values: { reference: referenceValues, product: productValues },
		});

		if (!hasValidationError) {
			const referenceUpdate = new ReferenceUpdate(
				values.reference.basketVersion,
				new GeneralInformation(
					values.reference.productName,
					values.reference.scientificName,
					values.reference.concentration,
					values.reference.pharmaceuticalForm,
					values.reference.packSize,
					values.reference.companyName
				),
				proposedCifPrice,
				new CountryOfOrigin(nationality?.alpha2 || null, exFactoryPrice, wholesalePrice, publicPrice)
			);
			onUpdate(this.props.group, referenceUpdate, products);
		}
	};

	handleCloseDetail = () => {
		const { detailProductId, onCloseDetail } = this.props;

		this.setState({ lastSeenProductIdDetail: detailProductId });
		onCloseDetail();
	};

	private handleProductsCopied = () => {
		this.setState((prevState) => ({
			checkedRows: [],
		}));
	};

	render() {
		const {
			props: {
				header,
				aeMaxPrice,
				referenceBasketVersion,
				group,
				pricedProducts,
				arabEmiratesProducts,
				referenceBasketProducts,
				otherProducts,
				isFetching,
				detailProductId,
				productDetailLink,
				detailProductTabName,
				currency,
				onShowDetail,
				onSave,
				onCurrencyChange,
			},
			state: { lastSelectedProductId, lastSeenProductIdDetail, removeProduct, checkedRows },
		} = this;

		if (aeMaxPrice === null && isFetching) {
			return <ActivityIndicator />;
		}

		const values = this.resolveValues();

		return (
			<Fragment>
				{header}

				<section className={styles.section}>
					{detailProductId ? (
						<PricesContainer currency={currency} productId={detailProductId}>
							<ProductProvider aeMaxPrice={aeMaxPrice} id={detailProductId}>
								{(product) => (
									<ProductDetailModal
										currency={currency}
										groupId={group.id}
										isFetching={false}
										product={product}
										productId={detailProductId}
										products={pricedProducts}
										selectedTabName={detailProductTabName}
										shareLink={`${window.location.origin}/search/${encodeURIComponent(detailProductId)}/${
											detailProductTabName === 'info' ? '' : encodeURIComponent(detailProductTabName)
										}`}
										onClose={this.handleCloseDetail}
										onCurrencyChange={onCurrencyChange}
										onChange={(productId) => onShowDetail(productId, detailProductTabName)}
										onSelectTab={(tabName) => onShowDetail(detailProductId, tabName)}
									/>
								)}
							</ProductProvider>
						</PricesContainer>
					) : null}

					{aeMaxPrice && referenceBasketVersion ? <Calculation aeMaxPrice={aeMaxPrice} /> : null}

					<ParametersPanel
						group={group}
						values={values.reference}
						onPriceChange={this.handleReferenceValueChange}
						onReferenceBasketVersionChange={this.handleReferenceBasketVersionChange}
						onSave={() => onSave(group)}
						onValueChange={this.handleReferenceValueChange}
					/>

					{pricedProducts.length === 0 ? <ProductInstructions /> : null}

					{pricedProducts.length > 0 ? (
						<div className={styles.tableHeader}>
							<div className={styles.in}>
								<div>
									<Heading level={2}>United Arab Emirates product</Heading>
								</div>
							</div>
						</div>
					) : null}

					{pricedProducts.length > 0 ? (
						<ProductsTable
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							lastSelectedProductId={lastSelectedProductId}
							products={arabEmiratesProducts}
							productValues={values.product}
							onChange={this.handleProductValuesChange}
							onSave={() => onSave(group)}
							onSelect={this.productSelected}
						/>
					) : null}

					{referenceBasketProducts.length > 0 ? (
						<div className={styles.tableHeader}>
							<div className={styles.in}>
								<Heading level={2}>Reference basket products</Heading>
							</div>
						</div>
					) : null}

					{referenceBasketProducts.length > 0 && referenceBasketProducts.length > 0 ? (
						<ProductsTable
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							lastSelectedProductId={lastSelectedProductId}
							products={referenceBasketProducts}
							productValues={values.product}
							onChange={this.handleProductValuesChange}
							onSave={() => onSave(group)}
							onSelect={this.productSelected}
						/>
					) : null}

					{otherProducts.length > 0 ? (
						<div className={styles.tableHeader}>
							<div className={styles.in}>
								<div>
									<Heading level={2}>Other</Heading>
								</div>
							</div>
						</div>
					) : null}

					{otherProducts.length > 0 ? (
						<ProductsTable
							disableSelection
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							lastSelectedProductId={lastSelectedProductId}
							products={otherProducts}
							productValues={values.product}
							onChange={this.handleProductValuesChange}
							onSave={() => onSave(group)}
							onSelect={this.productSelected}
						/>
					) : null}

					{checkedRows.length > 0 ? (
						<CopyGroupProductActionPanel
							group={group}
							products={checkedRows}
							selectedProductsCount={this.countRows()}
							onClickCancelDelete={this.onClickCancelDelete}
							onClickRemoveRow={this.onDeleteItems}
							onProductsCopied={this.handleProductsCopied}
						/>
					) : null}

					{removeProduct ? (
						<ProductGroupRemoveProductModal
							group={group}
							product={removeProduct}
							onClose={() => this.setState({ removeProduct: null })}
							onConfirm={() => onSave(group)}
							onSuccess={() => this.setState({ removeProduct: null })}
						/>
					) : null}
				</section>
			</Fragment>
		);
	}
}
